/** Graph invariants (checked by tests and the fuzzer) and a canonical form for equality checks. */
import type { Graph } from './graph.ts'
import type { NodeId } from './ids.ts'
import type { Node } from './model.ts'
import { compareKeys } from './order.ts'
import { hasTitle, titleKey } from './titles.ts'

/**
 * Returns a list of violations (empty = healthy):
 * 1. Blocks always have a parent; the parent exists.
 * 2. A live node never sits under a deleted parent (deletes cascade).
 * 3. The children index matches parentId exactly: every non-top-level node listed once, under
 *    its own parent, and nothing else listed.
 * 4. Sibling order keys are strictly increasing (no ties).
 * 5. No cycles.
 * 6. Live titles are unique (case-insensitive), so `[[Title]]` resolves to one node (D4).
 */
export function checkInvariants(g: Graph): string[] {
  const errs: string[] = []
  const seen = new Set<NodeId>()
  for (const pid of g.parentIds()) {
    const ks = g.children(pid)
    if (!g.has(pid)) errs.push(`children index has unknown parent ${pid}`)
    let prev: Node | undefined
    for (const k of ks) {
      const n = g.get(k)
      if (!n) {
        errs.push(`index lists unknown node ${k} under ${pid}`)
        continue
      }
      if (seen.has(k)) errs.push(`${k} listed twice in the children index`)
      seen.add(k)
      if (n.parentId !== pid) errs.push(`${k} listed under ${pid} but parentId is ${n.parentId}`)
      if (prev && compareKeys(prev.order, n.order) >= 0) {
        errs.push(`order not strictly increasing under ${pid}: ${prev.id}=${prev.order} ${k}=${n.order}`)
      }
      prev = n
    }
  }
  let withParent = 0
  for (const n of g.nodes()) {
    if (n.parentId === null) {
      if (n.kind === 'block') errs.push(`block ${n.id} has no parent`)
      continue
    }
    withParent++
    const p = g.get(n.parentId)
    if (!p) {
      errs.push(`${n.id}: parent ${n.parentId} does not exist`)
      continue
    }
    if (n.deletedAt === null && p.deletedAt !== null) errs.push(`live ${n.id} under deleted ${p.id}`)
    if (!seen.has(n.id)) errs.push(`${n.id} missing from the children index of ${n.parentId}`)
  }
  if (withParent !== seen.size) errs.push(`index size ${seen.size} != nodes with parent ${withParent}`)

  // Cycles: walk up from each node; 1 = on the current path, 2 = known to reach a root.
  const state = new Map<NodeId, 1 | 2>()
  for (const n of g.nodes()) {
    const path: NodeId[] = []
    let cur: NodeId | null = n.id
    while (cur !== null && state.get(cur) !== 2) {
      if (state.get(cur) === 1) {
        errs.push(`cycle through ${cur}`)
        break
      }
      state.set(cur, 1)
      path.push(cur)
      cur = g.get(cur)?.parentId ?? null
    }
    for (const id of path) state.set(id, 2)
  }

  const titles = new Map<string, NodeId>()
  for (const n of g.nodes()) {
    if (!hasTitle(n)) continue
    const k = titleKey(n.title!)
    const other = titles.get(k)
    if (other !== undefined) errs.push(`title "${n.title}" on both ${other} and ${n.id}`)
    else titles.set(k, n.id)
  }
  return errs
}

/**
 * JSON with object keys sorted at every level. Postgres jsonb reorders keys (by length, then
 * bytes), so equality of field values must not depend on key order.
 */
export function stableJson(v: unknown): string {
  if (v === null || typeof v !== 'object') return JSON.stringify(v) ?? 'null'
  if (Array.isArray(v)) return `[${v.map(stableJson).join(',')}]`
  const o = v as Record<string, unknown>
  return `{${Object.keys(o)
    .sort()
    .filter((k) => o[k] !== undefined)
    .map((k) => `${JSON.stringify(k)}:${stableJson(o[k])}`)
    .join(',')}}`
}

/** Canonical form of the live graph: every field except timestamps, sorted by id. */
export function canonical(g: Graph): string {
  const live: Node[] = []
  for (const n of g.nodes()) if (n.deletedAt === null) live.push(n)
  live.sort((a, b) => compareKeys(a.id, b.id))
  return stableJson(
    live.map((n) => [
      n.id,
      n.kind,
      n.parentId,
      n.order,
      n.content,
      n.title,
      n.collapsed,
      n.tags,
      n.props,
      n.date,
      n.private,
      n.origin,
    ]),
  )
}

/** Live pre-order of every node under `root` (hidden ones included), as ids. */
export function preorder(g: Graph, root: NodeId): NodeId[] {
  const out: NodeId[] = []
  const walk = (id: NodeId): void => {
    for (const k of g.children(id)) {
      if (!g.isLive(k)) continue
      out.push(k)
      walk(k)
    }
  }
  walk(root)
  return out
}
